import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import VendorMasterForm from "./VendorMasterForm";
import "./VendorEstimate.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.magnirauniverse.com";

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

const vendorColors = [
  "ve-vendor-blue",
  "ve-vendor-green",
  "ve-vendor-purple",
  "ve-vendor-orange",
  "ve-vendor-pink",
  "ve-vendor-cyan",
];

const getVendorColorClass = (vendorIndex = 0) =>
  vendorColors[vendorIndex % vendorColors.length];

const getDiffPercent = (targetCost, vendorAmount) => {
  const target = Number(targetCost || 0);
  const amount = Number(vendorAmount || 0);
  if (!target) return "0.00%";
  return `${(((target - amount) / target) * 100).toFixed(2)}%`;
};

const buildRowsFromQuote = (quote) =>
  (quote?.items || []).map((item) => ({
    selected: true,
    itemName: item.itemName || item.label || item.name || "",
    description: item.description || "",
    qty: item.qty || item.quantity || 1,
    length: item.length || "",
    width: item.width || "",
    area:
      item.area ||
      (item.length && item.width
        ? Number(item.length) * Number(item.width)
        : ""),
    targetCost:
      item.targetCost || item.vendorTargetCost || item.MRP || item.price || "",
  }));

const VendorEstimate = ({ project }) => {
  const navigate = useNavigate();

  const [approvedQuotes, setApprovedQuotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [items, setItems] = useState([]);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [sendingMail, setSendingMail] = useState(false);
  const [submittedEstimates, setSubmittedEstimates] = useState([]);
  const [lockedItems, setLockedItems] = useState({});

  const getAuthToken = () => {
    let storedUser = {};
    try {
      storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      storedUser = {};
    }

    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("userToken") ||
      localStorage.getItem("adminToken") ||
      storedUser?.token ||
      storedUser?.accessToken ||
      ""
    );
  };

  const getAuthConfig = () => {
    const token = getAuthToken();

    return token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : { headers: {} };
  };

  const asArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.quotes)) return data.quotes;
    if (Array.isArray(data?.vendors)) return data.vendors;
    if (Array.isArray(data?.estimates)) return data.estimates;
    if (Array.isArray(data?.pos)) return data.pos;
    if (data && typeof data === "object") return [data];
    return [];
  };

  const logApiError = (label, err) => {
    console.error(label, {
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      data: err?.response?.data,
      message: err?.message,
    });
  };

  const getVendorEmail = (vendor) =>
    vendor?.email ||
    vendor?.vendorEmail ||
    vendor?.mail ||
    vendor?.contactEmail ||
    vendor?.emailId ||
    "";

  const getSelectedVendor = () =>
    vendors.find((v) => String(v._id) === String(selectedVendorId));

  const openVendorPO = (vendorId, vendorName) => {
    const vendor = vendors.find((v) => String(v._id) === String(vendorId)) || {
      _id: vendorId,
      fullName: vendorName,
    };

    navigate(`/vendor-po/${vendorId}`, {
      state: { project, vendor },
    });
  };

  const fetchApprovedQuotes = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/quotes?projectName=${encodeURIComponent(
          project?.name || "",
        )}`,
        getAuthConfig(),
      );

      const approved = asArray(res.data).filter(
        (q) =>
          q.status === "APPROVED" ||
          q.status === "Approved" ||
          q.isApproved === true,
      );

      setApprovedQuotes(approved);

      if (approved.length > 0) {
        const firstQuote = approved[0];
        setSelectedQuote(firstQuote);
        setItems(buildRowsFromQuote(firstQuote));
      }
    } catch (err) {
      logApiError("Approved quotes fetch error", err);
      setApprovedQuotes([]);
      setSelectedQuote(null);
      setItems([]);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/vendors`, getAuthConfig());
      setVendors(asArray(res.data));
    } catch (err) {
      logApiError("Vendor fetch error", err);
      setVendors([]);
    }
  };

  const fetchSubmittedEstimates = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/vendor-estimate?projectName=${encodeURIComponent(
          project?.name || "",
        )}`,
        getAuthConfig(),
      );

      const submitted = asArray(res.data).filter(
        (x) =>
          x.status === "SUBMITTED" &&
          x.vendorId !== "FINAL_LOCKED" &&
          x.vendorName !== "FINAL_LOCKED" &&
          x.vendorId !== "FINAL_LOCKED_PO" &&
          x.vendorName !== "FINAL_LOCKED_PO",
      );

      const latestByVendor = {};

      submitted.forEach((estimate) => {
        const key = estimate.vendorId || estimate.vendorName;

        const currentTime = new Date(
          estimate.submittedAt || estimate.updatedAt || estimate.createdAt || 0,
        );

        const existingTime = latestByVendor[key]
          ? new Date(
              latestByVendor[key].submittedAt ||
                latestByVendor[key].updatedAt ||
                latestByVendor[key].createdAt ||
                0,
            )
          : null;

        if (!latestByVendor[key] || currentTime > existingTime) {
          latestByVendor[key] = estimate;
        }
      });

      setSubmittedEstimates(Object.values(latestByVendor));
    } catch (err) {
      if (err?.response?.status === 404) {
        setSubmittedEstimates([]);
        return;
      }

      logApiError("Comparison fetch error", err);
      setSubmittedEstimates([]);
    }
  };

  const fetchSavedLockedPOs = async () => {
    try {
      if (!project?.name) return;

      const res = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      const poList = asArray(res.data);

      const restoredLocks = {};

      poList.forEach((po) => {
        (po.items || []).forEach((item) => {
          if (item.source === "VENDOR_ESTIMATE_LOCKED") {
            restoredLocks[item.itemName] = {
              vendorId: po.vendorId || item.vendorId,
              vendorName: po.vendorName || item.vendorName,
              vendorCode: po.vendorCode || item.vendorCode,
              itemName: item.itemName,
              qty: item.qty,
              targetCost: item.vendorTargetCost || item.targetCost || 0,
              vendorRate: item.vendorRate || 0,
              vendorAmount: item.vendorAmount || 0,
              difference:
                Number(item.vendorTargetCost || item.targetCost || 0) -
                Number(item.vendorAmount || 0),
            };
          }
        });
      });

      setLockedItems(restoredLocks);
    } catch (err) {
      if (err?.response?.status === 404) {
        setLockedItems({});
        return;
      }

      logApiError("Fetch saved locked PO error", err);
      setLockedItems({});
    }
  };

  useEffect(() => {
    if (project?.name) {
      fetchApprovedQuotes();
      fetchVendors();
      fetchSubmittedEstimates();
      fetchSavedLockedPOs();
    }
  }, [project?.name]);

  const handleSelectQuote = (quoteId) => {
    const quote = approvedQuotes.find((q) => q._id === quoteId);

    setSelectedQuote(quote || null);
    setGeneratedLink("");
    setItems(buildRowsFromQuote(quote));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
    setGeneratedLink("");
  };

  const selectAllItems = () => {
    setItems((prev) => prev.map((item) => ({ ...item, selected: true })));
    setGeneratedLink("");
  };

  const selectNoItems = () => {
    setItems((prev) => prev.map((item) => ({ ...item, selected: false })));
    setGeneratedLink("");
  };

  const generateVendorForm = async () => {
    try {
      if (!selectedQuote) return alert("Please select approved quote.");
      if (!selectedVendorId) return alert("Please select vendor.");

      const selectedItems = items.filter((i) => i.selected);

      if (selectedItems.length === 0) {
        return alert("Please select at least one item.");
      }

      const vendor = getSelectedVendor();
      if (!vendor) return alert("Vendor not found.");

      const payload = {
        projectId: project?._id || "",
        projectName: project?.name || "",
        quoteId: selectedQuote?._id || "",
        vendorId: vendor?._id || "",
        vendorName: vendor?.fullName || vendor?.vendorName || "",
        vendorCode: vendor?.vendorCode || "",
        vendorEmail: getVendorEmail(vendor),
        items: selectedItems,
      };

      const res = await axios.post(
        `${BASE_URL}/api/vendor-estimate/generate-link`,
        payload,
        getAuthConfig(),
      );

      setGeneratedLink(res.data.formLink);
      alert("Vendor form link generated.");
    } catch (err) {
      logApiError("Generate vendor form error", err);
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to generate vendor form.",
      );
    }
  };

  const sendToVendor = async () => {
    try {
      const vendor = getSelectedVendor();

      if (!vendor) return alert("Please select vendor.");
      if (!generatedLink)
        return alert("Please generate vendor form link first.");

      const email = getVendorEmail(vendor);

      if (!email) {
        navigator.clipboard.writeText(generatedLink);
        return alert("Vendor email not found. Link copied.");
      }

      setSendingMail(true);

      await axios.post(
        `${BASE_URL}/api/vendor-estimate/send-mail`,
        {
          vendorEmail: email,
          vendorName: vendor?.fullName || vendor?.vendorName || "Vendor",
          projectName: project?.name || "",
          formLink: generatedLink,
        },
        getAuthConfig(),
      );

      alert(
        "Your email has been submitted successfully. It should reach the vendor shortly.",
      );
    } catch (err) {
      const issue = err?.response?.data || {};
      console.error("Send mail error FULL:", {
        status: err?.response?.status,
        upstreamStatus: issue.upstreamStatus,
        code: issue.code,
        brevoCode: issue.brevoCode,
        message: issue.message || err?.message,
        url: err?.config?.url,
      });

      // Show the actual upstream problem rather than hiding it behind generic HTTP 500.
      const detail = [issue.message, issue.brevoMessage]
        .filter(Boolean)
        .join("\n");
      alert(detail || "Failed to send mail. Check backend logs.");
    } finally {
      setSendingMail(false);
    }
  };

  const copyLink = async () => {
    if (!generatedLink) return;
    await navigator.clipboard.writeText(generatedLink);
    alert("Link copied.");
  };

  const lockVendorForItem = (itemName, vendorData, itemData, baseItem) => {
    setLockedItems((prev) => ({
      ...prev,
      [itemName]: {
        vendorId: vendorData.vendorId,
        vendorName: vendorData.vendorName,
        vendorCode: vendorData.vendorCode,
        itemName,
        qty: baseItem.qty,
        targetCost: baseItem.targetCost,
        vendorRate: itemData.vendorRate,
        vendorAmount: itemData.vendorAmount,
        difference:
          Number(baseItem.targetCost || 0) - Number(itemData.vendorAmount || 0),
      },
    }));
  };
  const requestRevision = async (vendor) => {
    try {
      const note = prompt("Please enter your revision request message:");

      if (!note) return;

      const res = await axios.post(
        `${BASE_URL}/api/vendor-estimate/request-revision/${vendor._id}`,
        { note, sendMail: true },
        getAuthConfig(),
      );

      alert("Revision request sent to vendor.");

      await fetchSubmittedEstimates();
    } catch (err) {
      logApiError("Revision request error", err);
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to request revision.",
      );
    }
  };
  const saveLockedFinalQuote = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        return alert("Login token not found. Please login again.");
      }

      const lockedArray = Object.values(lockedItems);

      if (lockedArray.length === 0) {
        return alert("Please lock at least one vendor item.");
      }

      const groupedByVendor = {};

      lockedArray.forEach((item) => {
        if (!groupedByVendor[item.vendorId]) {
          groupedByVendor[item.vendorId] = {
            vendorId: item.vendorId,
            vendorName: item.vendorName,
            vendorCode: item.vendorCode || "",
            items: [],
          };
        }

        groupedByVendor[item.vendorId].items.push({
          vendorId: item.vendorId,
          vendorName: item.vendorName,
          vendorCode: item.vendorCode || "",
          itemName: item.itemName,
          so:
            selectedQuote?.quoteNo ||
            selectedQuote?.soNumber ||
            selectedQuote?.salesOrderNo ||
            `SO-${String(selectedQuote?._id || "").slice(-6)}`,
          qty: Number(item.qty || 1),
          vendorTargetCost: Number(item.targetCost || 0),
          targetCost: Number(item.targetCost || 0),
          vendorQty: Number(item.qty || 1),
          vendorRate: Number(item.vendorRate || 0),
          vendorAmount: Number(item.vendorAmount || 0),
          difference:
            Number(item.targetCost || 0) - Number(item.vendorAmount || 0),
          source: "VENDOR_ESTIMATE_LOCKED",
        });
      });

      for (const group of Object.values(groupedByVendor)) {
        const totalAmount = group.items.reduce(
          (sum, row) => sum + Number(row.vendorAmount || 0),
          0,
        );

        await axios.post(
          `${BASE_URL}/api/vendor-po/save`,
          {
            projectName: project?.name || "",
            vendorId: group.vendorId,
            vendorName: group.vendorName,
            vendorCode: group.vendorCode,
            items: group.items,
            totalAmount,
          },
          getAuthConfig(),
        );
      }

      await fetchSubmittedEstimates();
      await fetchSavedLockedPOs();

      alert("Locked items saved in Vendor PO successfully.");
    } catch (err) {
      logApiError("Save locked quote to Vendor PO error", err);

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          `Failed to save locked items in Vendor PO${
            err?.response?.status ? ` (HTTP ${err.response.status})` : ""
          }.`,
      );
    }
  };

  const selectedCount = useMemo(
    () => items.filter((item) => item.selected).length,
    [items],
  );

  const finalQuoteItems = useMemo(
    () => Object.values(lockedItems),
    [lockedItems],
  );

  const finalQuoteTotal = useMemo(
    () =>
      finalQuoteItems.reduce(
        (sum, item) => sum + Number(item.vendorAmount || 0),
        0,
      ),
    [finalQuoteItems],
  );

  return (
    <div className="ve-page">
      <div className="ve-header">
        <div>
          <h2 className="ve-title">Vendor Estimate</h2>
          <div className="ve-subtitle">
            Project: {project?.name || "-"} | Selected Items: {selectedCount}
          </div>
        </div>

        <button
          className="btn btn-success"
          onClick={() => setShowVendorForm(true)}
        >
          + Vendor Master
        </button>
      </div>

      <div className="ve-card">
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="ve-label">Approved Quote</div>
            <select
              className="form-control"
              value={selectedQuote?._id || ""}
              onChange={(e) => handleSelectQuote(e.target.value)}
            >
              <option value="">Select Approved Quote</option>
              {approvedQuotes.map((q) => (
                <option key={q._id} value={q._id}>
                  {q.projectName || project?.name} - {q.quoteNo || q._id}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 mb-3">
            <div className="ve-label">Select Vendor</div>
            <select
              className="form-control"
              value={selectedVendorId}
              onChange={(e) => {
                setSelectedVendorId(e.target.value);
                setGeneratedLink("");
              }}
            >
              <option value="">Select Vendor Name</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {(v.vendorCode || "NA") +
                    " — " +
                    (v.fullName || v.vendorName || "Vendor") +
                    (getVendorEmail(v) ? ` — ${getVendorEmail(v)}` : "")}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedVendorId && (
          <div className="ve-vendor-email">
            Selected Vendor Email:{" "}
            {getVendorEmail(getSelectedVendor()) ||
              "Email not found in vendor master"}
          </div>
        )}

        <div className="ve-select-actions">
          <button
            className="btn btn-sm btn-outline-success"
            onClick={selectAllItems}
          >
            Select All
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={selectNoItems}
          >
            Select None
          </button>
        </div>

        <div className="ve-table-wrap">
          <table className="table table-bordered table-striped ve-table">
            <thead className="text-center">
              <tr>
                <th className="ve-th-light">Select</th>
                <th className="ve-th-light">Sr No</th>
                <th className="ve-th-light">Item</th>
                <th className="ve-th-light">Description</th>
                <th className="ve-th-light">Qty</th>
                <th className="ve-th-light">Length</th>
                <th className="ve-th-light">Width</th>
                <th className="ve-th-light">Area</th>
                <th className="ve-th-light">Target Cost</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center fw-bold py-4">
                    No approved quote items found.
                  </td>
                </tr>
              ) : (
                items.map((row, index) => (
                  <tr
                    key={index}
                    className={row.selected ? "ve-row-active" : "ve-row-muted"}
                  >
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={Boolean(row.selected)}
                        onChange={(e) =>
                          updateItem(index, "selected", e.target.checked)
                        }
                      />
                    </td>

                    <td className="text-center fw-bold">{index + 1}</td>
                    <td className="ve-item-cell">{row.itemName || "-"}</td>
                    <td className="ve-desc-cell">
                      {String(row.description || "-").replace(
                        /<br\s*\/?>/gi,
                        "\n",
                      )}
                    </td>
                    <td className="text-center">{row.qty || 0}</td>
                    <td className="text-center">{row.length || "-"}</td>
                    <td className="text-center">{row.width || "-"}</td>
                    <td className="text-center">{row.area || "-"}</td>
                    <td className="text-end fw-bold">
                      {money(row.targetCost)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {items.length > 0 && (
          <div className="ve-btn-row">
            <button className="btn btn-primary" onClick={generateVendorForm}>
              Generate Vendor Form
            </button>

            <button
              className="btn btn-warning"
              onClick={sendToVendor}
              disabled={sendingMail}
            >
              {sendingMail ? "Sending..." : "Send To Vendor"}
            </button>

            {generatedLink && (
              <button className="btn btn-secondary" onClick={copyLink}>
                Copy Link
              </button>
            )}

            <button
              className="btn btn-outline-dark"
              onClick={() => {
                fetchSubmittedEstimates();
                fetchSavedLockedPOs();
              }}
            >
              Refresh Comparison
            </button>
          </div>
        )}

        {generatedLink && (
          <div className="ve-link-box">
            Generated Link:
            <div className="ve-break">{generatedLink}</div>
          </div>
        )}
      </div>

      <div className="ve-card">
        <h4 className="ve-section-title">Vendor Comparison Sheet</h4>

        <div className="ve-table-wrap">
          <table className="table table-bordered table-striped ve-table">
            <thead className="text-center">
              <tr>
                <th className="ve-th-dark">Sr No</th>
                <th className="ve-th-dark">Item</th>
                <th className="ve-th-dark">Qty</th>
                <th className="ve-th-dark">Target Cost</th>

                {submittedEstimates.map((vendor, vendorIndex) => {
                  const colorClass = getVendorColorClass(vendorIndex);

                  return (
                    <React.Fragment key={vendor._id}>
                      <th className={`ve-th-dark ${colorClass}`}>
                        {vendor.vendorName} Rate
                      </th>
                      <th className={`ve-th-dark ${colorClass}`}>
                        {vendor.vendorName} Amount
                      </th>
                      <th className={`ve-th-dark ${colorClass}`}>Difference</th>
                      <th className={`ve-th-dark ${colorClass}`}>Lock</th>
                    </React.Fragment>
                  );
                })}

                <th className="ve-th-dark">Final Vendor</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5 + submittedEstimates.length * 4}
                    className="text-center fw-bold py-4"
                  >
                    No items found for comparison.
                  </td>
                </tr>
              ) : submittedEstimates.length === 0 ? (
                <tr>
                  <td colSpan="20" className="text-center fw-bold py-4">
                    No vendor submitted rates yet.
                  </td>
                </tr>
              ) : (
                items.map((baseItem, index) => {
                  const locked = lockedItems[baseItem.itemName];

                  return (
                    <tr key={index} className={locked ? "ve-locked-row" : ""}>
                      <td className="text-center fw-bold">{index + 1}</td>
                      <td className="ve-item-cell">{baseItem.itemName}</td>
                      <td className="text-center">{baseItem.qty}</td>
                      <td className="text-end fw-bold">
                        {money(baseItem.targetCost)}
                      </td>

                      {submittedEstimates.map((vendor, vendorIndex) => {
                        const colorClass = getVendorColorClass(vendorIndex);

                        const vendorItem = (vendor.items || []).find(
                          (x) => x.itemName === baseItem.itemName,
                        );

                        const vendorRate = Number(vendorItem?.vendorRate || 0);
                        const vendorAmount = Number(
                          vendorItem?.vendorAmount || 0,
                        );
                        const difference =
                          Number(baseItem.targetCost || 0) - vendorAmount;

                        const isLockedVendor =
                          locked &&
                          String(locked.vendorId) === String(vendor.vendorId);

                        return (
                          <React.Fragment key={vendor._id}>
                            <td className={`text-end ${colorClass}`}>
                              {vendorItem ? money(vendorRate) : "-"}
                            </td>

                            <td className={`text-end fw-bold ${colorClass}`}>
                              {vendorItem ? money(vendorAmount) : "-"}
                            </td>

                            <td className={`text-end fw-bold ${colorClass}`}>
                              {vendorItem ? (
                                <span
                                  className={
                                    difference >= 0
                                      ? "ve-diff-positive"
                                      : "ve-diff-negative"
                                  }
                                >
                                  {money(difference)}
                                  <br />
                                  <small>
                                    {getDiffPercent(
                                      baseItem.targetCost,
                                      vendorAmount,
                                    )}
                                  </small>
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>

                            <td className={`text-center ${colorClass}`}>
                              {vendorItem ? (
                                <>
                                  <button
                                    className={
                                      isLockedVendor
                                        ? "btn btn-sm btn-dark"
                                        : "btn btn-sm btn-success"
                                    }
                                    onClick={() =>
                                      lockVendorForItem(
                                        baseItem.itemName,
                                        vendor,
                                        vendorItem,
                                        baseItem,
                                      )
                                    }
                                  >
                                    {isLockedVendor ? "Locked" : "Lock"}
                                  </button>

                                  <button
                                    className="btn btn-sm btn-warning mt-1"
                                    onClick={() => requestRevision(vendor)}
                                  >
                                    Ask Revision
                                  </button>
                                </>
                              ) : (
                                "-"
                              )}
                            </td>
                          </React.Fragment>
                        );
                      })}

                      <td className="text-center">
                        {locked ? (
                          <button
                            className="ve-vendor-link"
                            onClick={() =>
                              openVendorPO(locked.vendorId, locked.vendorName)
                            }
                          >
                            {locked.vendorName}
                          </button>
                        ) : (
                          <span className="ve-pending-badge">Pending</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {Object.keys(lockedItems).length > 0 && (
          <div className="ve-btn-row">
            <button className="btn btn-success" onClick={saveLockedFinalQuote}>
              Save Locked Final Vendor Quote
            </button>
          </div>
        )}
      </div>

      <div className="ve-card">
        <h4 className="ve-section-title">Final Locked Vendor Quote</h4>

        {finalQuoteItems.length === 0 ? (
          <div className="text-center fw-bold py-4">
            No final vendor quote locked yet.
          </div>
        ) : (
          <div className="ve-table-wrap">
            <table className="table table-bordered table-striped ve-table">
              <thead className="text-center">
                <tr>
                  <th className="ve-th-dark">Sr No</th>
                  <th className="ve-th-dark">Item</th>
                  <th className="ve-th-dark">Final Vendor</th>
                  <th className="ve-th-dark">Qty</th>
                  <th className="ve-th-dark">Target Cost</th>
                  <th className="ve-th-dark">Vendor Rate</th>
                  <th className="ve-th-dark">Vendor Amount</th>
                  <th className="ve-th-dark">Difference</th>
                </tr>
              </thead>

              <tbody>
                {finalQuoteItems.map((item, index) => {
                  const vendorIndex = submittedEstimates.findIndex(
                    (v) => String(v.vendorId) === String(item.vendorId),
                  );

                  const colorClass = getVendorColorClass(
                    vendorIndex >= 0 ? vendorIndex : index,
                  );

                  return (
                    <tr key={item.itemName} className={colorClass}>
                      <td className="text-center fw-bold">{index + 1}</td>
                      <td>{item.itemName}</td>
                      <td>
                        <button
                          className="ve-vendor-link"
                          onClick={() =>
                            openVendorPO(item.vendorId, item.vendorName)
                          }
                        >
                          {item.vendorName}
                        </button>
                      </td>
                      <td className="text-center">{item.qty}</td>
                      <td className="text-end">{money(item.targetCost)}</td>
                      <td className="text-end">{money(item.vendorRate)}</td>
                      <td className="text-end fw-bold">
                        {money(item.vendorAmount)}
                      </td>
                      <td className="text-end fw-bold">
                        <span
                          className={
                            Number(item.difference || 0) >= 0
                              ? "ve-diff-positive"
                              : "ve-diff-negative"
                          }
                        >
                          {money(item.difference)}
                          <br />
                          <small>
                            {getDiffPercent(item.targetCost, item.vendorAmount)}
                          </small>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="ve-total-row">
                  <td colSpan="6" className="text-end fw-bold">
                    Final Quote Total
                  </td>
                  <td className="text-end fw-bold">{money(finalQuoteTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {showVendorForm && (
        <div className="ve-modal-backdrop">
          <div className="ve-modal">
            <button
              className="btn btn-danger mb-3 float-end"
              onClick={() => {
                setShowVendorForm(false);
                fetchVendors();
              }}
            >
              Close
            </button>

            <VendorMasterForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorEstimate;
